  (() => {
    if (window.__kindalovelyProductScriptLoaded) return;
    window.__kindalovelyProductScriptLoaded = true;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const { validVariantSelection, cartLineItems, offerPricing } = window.KindaLovelyProductPricing;

    const formatMoney = (cents, root) => {
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        return window.Shopify.formatMoney(cents, root.dataset.moneyFormat);
      }

      const currency = root.dataset.currency || 'USD';
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency,
        }).format(Number(cents) / 100);
      } catch {
        return `${(Number(cents) / 100).toFixed(2)} ${currency}`;
      }
    };

    const initProduct = (root) => {
      if (!root || root.dataset.initialized === 'true') return;
      root.dataset.initialized = 'true';

      const form = root.querySelector('[data-product-form]');
      const variantInput = root.querySelector('[data-variant-select]');
      const quantityInput = root.querySelector('[data-quantity-input]');
      const offerInputs = [...root.querySelectorAll('[data-offer-quantity]')];
      const addButton = root.querySelector('[data-add-button]');
      const addPrice = addButton?.querySelector('[data-add-price]');
      const cartDialog = document.querySelector('[data-cart-dialog]');
      const priceNode = root.querySelector('[data-current-price]');
      const compareNode = root.querySelector('[data-compare-price]');
      const saleBadge = root.querySelector('[data-sale-badge]');
      const savingsBadge = root.querySelector('[data-price-savings]');
      const restockNotice = root.querySelector('[data-restock-notice]');
      const restockDate = restockNotice?.querySelector('[data-restock-date]');
      if (restockDate) {
        const visitedAt = new Date();
        const year = visitedAt.getFullYear();
        const month = visitedAt.getMonth() + 2;
        const lastDay = new Date(year, month + 1, 0).getDate();
        const date = new Date(year, month, Math.min(visitedAt.getDate(), lastDay));
        restockDate.dateTime = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
        restockDate.textContent = new Intl.DateTimeFormat(document.documentElement.lang || 'en', {
          month: 'long', year: 'numeric',
        }).format(date);
        restockNotice.hidden = false;
      }
      let selectedQuantity = 1;
      let pending = false;

      window.KindaLovelyProductStickyOffer.init(root, reducedMotion);

      const variantOptions = variantInput?.tagName === 'SELECT' ? [...variantInput.options] : variantInput ? [variantInput] : [];
      const bundleRows = [...root.querySelectorAll('[data-bundle-item]')];
      const bundleOptionControls = [...root.querySelectorAll('[data-bundle-option]')];
      const bundleOptionDropdowns = [...root.querySelectorAll('[data-bundle-select]')];
      const sharedBundleOptions = root.querySelector('[data-bundle-options]');

      const readVariant = (option) => {
        if (!option) return null;
        return {
          id: option.value,
          price: Number(option.dataset.priceCents || 0),
          compareAt: Number(option.dataset.compareCents || 0),
          available: option.dataset.available === 'true',
          mediaId: option.dataset.mediaId,
          formattedPrice: option.dataset.priceFormatted || '',
          formattedCompareAt: option.dataset.compareFormatted || '',
          unitPriceLabel: option.dataset.unitPriceLabel || '',
          min: Math.max(1, Number(option.dataset.minQuantity || 1)),
          max: Number(option.dataset.maxQuantity || 0),
          increment: Math.max(1, Number(option.dataset.quantityIncrement || 1)),
          title: option.textContent.trim(),
          optionElement: option,
        };
      };

      const getSelectedVariant = () => {
        if (!variantInput) return null;
        const option = variantInput.tagName === 'SELECT'
          ? variantInput.options[variantInput.selectedIndex]
          : variantInput;
        return readVariant(option);
      };

      const syncBundleOptionDropdown = (select) => {
        const dropdown = select.closest('[data-bundle-select]');
        if (!dropdown) return;
        const selectedOption = [...select.options].find((option) => option.value === select.value);
        const selectedValue = selectedOption?.textContent.trim() || select.value;
        const trigger = dropdown.querySelector('[data-bundle-option-trigger]');
        const valueNode = dropdown.querySelector('[data-bundle-option-current]');
        if (valueNode) valueNode.textContent = dropdown.hasAttribute('data-size-option') ? selectedValue.toUpperCase() : selectedValue;
        const selectedChoice = [...dropdown.querySelectorAll('[data-bundle-option-choice]')]
          .find((choice) => choice.dataset.optionValue === select.value);
        const triggerSwatch = trigger?.querySelector('.pdp__color-swatch');
        const choiceSwatch = selectedChoice?.querySelector('.pdp__color-swatch');
        if (triggerSwatch && choiceSwatch) triggerSwatch.replaceWith(choiceSwatch.cloneNode(true));
        if (trigger) {
          trigger.disabled = select.disabled;
          trigger.setAttribute('aria-label', `${trigger.dataset.optionLabel}: ${selectedValue}`);
        }
        dropdown.querySelectorAll('[data-bundle-option-choice]').forEach((choice) => {
          const option = [...select.options].find((selectOption) => selectOption.value === choice.dataset.optionValue);
          const selected = choice.dataset.optionValue === select.value;
          choice.disabled = !option || option.disabled || select.disabled;
          choice.setAttribute('aria-selected', String(selected));
          choice.tabIndex = selected && !choice.disabled ? 0 : -1;
        });
      };

      const getRowSelections = (row) => {
        const selected = {};
        row.querySelectorAll('[data-bundle-option]').forEach((control) => {
          if (control.tagName === 'SELECT' || control.checked) selected[control.dataset.optionPosition] = control.value;
        });
        return selected;
      };

      const rowMatchesVariant = (row, variantOption, selected, throughPosition = 3) =>
        [...row.querySelectorAll('[data-bundle-option]')].every((control) => {
          const position = Number(control.dataset.optionPosition);
          return position > throughPosition || selected[position] === variantOption.dataset[`option${position}`];
        });

      const findRowVariantOption = (row, selected, changedControl = null) => {
        const controls = [...row.querySelectorAll('[data-bundle-option]')];
        const exactAvailable = variantOptions.find((option) => option.dataset.available === 'true' && rowMatchesVariant(row, option, selected));
        if (exactAvailable) return exactAvailable;
        const throughPosition = changedControl ? Number(changedControl.dataset.optionPosition) : 3;
        return variantOptions.find((option) => option.dataset.available === 'true' && rowMatchesVariant(row, option, selected, throughPosition))
          || variantOptions.find((option) => rowMatchesVariant(row, option, selected, throughPosition))
          || (controls.length === 0 ? variantOptions[0] : null);
      };

      const getBundleItemVariant = (row) => {
        if (!row || !row.querySelector('[data-bundle-option]')) return getSelectedVariant();
        const selected = getRowSelections(row);
        const option = variantOptions.find((variantOption) => rowMatchesVariant(row, variantOption, selected));
        return readVariant(option);
      };

      const updateBundleRowAvailability = (row) => {
        const controls = [...row.querySelectorAll('[data-bundle-option]')];
        const selected = getRowSelections(row);
        controls.forEach((control) => {
          const position = Number(control.dataset.optionPosition);
          const setAvailability = (optionControl, value) => {
            optionControl.disabled = !variantOptions.some((variantOption) => {
              if (variantOption.dataset.available !== 'true' || variantOption.dataset[`option${position}`] !== value) return false;
              return controls.every((previousControl) => {
                const previousPosition = Number(previousControl.dataset.optionPosition);
                return previousPosition >= position || selected[previousPosition] === variantOption.dataset[`option${previousPosition}`];
              });
            });
          };

          if (control.tagName === 'SELECT') {
            [...control.options].forEach((option) => { setAvailability(option, option.value); });
          } else {
            setAvailability(control, control.value);
          }
          syncBundleOptionDropdown(control);
        });
      };

      const updateBundleRow = (row, changedControl = null) => {
        const selected = getRowSelections(row);
        const nextOption = findRowVariantOption(row, selected, changedControl);
        if (!nextOption) return null;
        row.querySelectorAll('[data-bundle-option]').forEach((control) => {
          const position = Number(control.dataset.optionPosition);
          const value = nextOption.dataset[`option${position}`];
          if (control.tagName === 'SELECT') control.value = value;
          else control.checked = control.value === value;
        });
        updateBundleRowAvailability(row);
        return readVariant(nextOption);
      };

      const selectedOffer = () => offerInputs.find((input) => input.checked && !input.disabled);
      const getOfferCard = (input = selectedOffer()) => input?.closest('[data-offer-card]');
      const syncSelectedBundleOptions = (activeCard) => {
        if (!sharedBundleOptions || !activeCard) return;
        closeBundleOptionDropdowns();
        activeCard.insertBefore(sharedBundleOptions, activeCard.querySelector('.pdp__offer-shipping'));
        const quantity = Number(activeCard.querySelector('[data-offer-quantity]')?.value || 1);
        bundleRows.forEach((row, index) => { row.hidden = index >= quantity; });
      };

      const getOfferVariants = (card) => {
        if (!card) return [];
        const quantity = Number(card.querySelector('[data-offer-quantity]')?.value || 1);
        if (bundleRows.length) return bundleRows.slice(0, quantity).map(getBundleItemVariant);
        const variant = getSelectedVariant();
        return Array.from({ length: quantity }, () => variant);
      };

      const isOfferValid = (card) => {
        if (!card) return false;
        const input = card.querySelector('[data-offer-quantity]');
        const quantity = Number(input?.value || 0);
        const variants = getOfferVariants(card);
        return variants.length === quantity && validVariantSelection(variants);
      };

      const getCartLineItems = () => {
        let variants;
        if (offerInputs.length) {
          const card = getOfferCard();
          if (!isOfferValid(card)) return null;
          variants = getOfferVariants(card);
        } else {
          const variant = getSelectedVariant();
          variants = Array.from({ length: selectedQuantity }, () => variant);
          if (!validVariantSelection(variants)) return null;
        }

        return cartLineItems(variants);
      };

      const updateOfferPrices = () => {
        const pricedOffers = [...root.querySelectorAll('[data-offer-card]')].map((card) => {
          const input = card.querySelector('[data-offer-quantity]');
          const discountPercent = Math.min(100, Math.max(0, Number(input.dataset.discountPercent || 0)));
          const variants = getOfferVariants(card);
          const pricing = offerPricing(variants, discountPercent);
          const valid = isOfferValid(card);
          return { card, input, pricing, valid, quantity: variants.length };
        });
        const eligible = pricedOffers.filter(({ valid, quantity }) => valid && quantity > 0);
        const bestOffer = eligible.find((candidate) => candidate.quantity > 1 && candidate.pricing.savings > 0 && eligible.every((other) =>
          other === candidate || candidate.pricing.total * other.quantity < other.pricing.total * candidate.quantity));

        pricedOffers.forEach(({ card, input, pricing, valid, quantity }) => {
          input.disabled = !valid;
          card.setAttribute('aria-disabled', String(!valid));

          const total = card.querySelector('[data-offer-total]');
          if (total) total.textContent = formatMoney(pricing.total, root);
          const compare = card.querySelector('[data-offer-compare]');
          if (compare) {
            compare.textContent = pricing.referenceTotal > pricing.total ? formatMoney(pricing.referenceTotal, root) : '';
            compare.hidden = pricing.referenceTotal <= pricing.total;
          }
          const savingBadge = card.querySelector('[data-offer-saving-badge]');
          if (savingBadge) {
            const percent = pricing.referenceTotal > 0 ? Math.floor(pricing.savings * 100 / pricing.referenceTotal) : 0;
            const percentFormat = input.dataset.savingsFormat === 'percent';
            savingBadge.hidden = pricing.savings <= 0 || (percentFormat && percent <= 0);
            savingBadge.textContent = savingBadge.hidden ? '' : percentFormat
              ? root.dataset.offerSavingsPercentBadgeFormat.replace('__percent__', String(percent))
              : root.dataset.offerSavingsFormat.replace('__amount__', formatMoney(pricing.savings, root));
            savingBadge.classList.toggle('pdp__offer-saving-badge--best', !savingBadge.hidden && bestOffer?.card === card);
          }
        });
      };

      const updateQuantity = () => {
        let checked = selectedOffer();
        if (offerInputs.length && !checked) {
          checked = offerInputs.find((input) => !input.disabled);
          if (checked) checked.checked = true;
        }
        if (checked) selectedQuantity = Number(checked.value);
        else if (!offerInputs.length) {
          const variant = getSelectedVariant();
          selectedQuantity = variant ? variant.min : 1;
        } else if (offerInputs[0]) selectedQuantity = Number(offerInputs[0].value);

        if (quantityInput) quantityInput.value = String(selectedQuantity);
        root.querySelectorAll('[data-quantity-promotion]').forEach((message) => {
          const minimum = Number(message.dataset.minQuantity || 1);
          const maximum = Number(message.dataset.maxQuantity || 0);
          message.hidden = selectedQuantity < minimum || (maximum > 0 && selectedQuantity > maximum);
        });
      };

      const updateVariant = () => {
        updateOfferPrices();
        let activeInput = selectedOffer();
        if (offerInputs.length && !activeInput) {
          activeInput = offerInputs.find((input) => !input.disabled);
          if (activeInput) activeInput.checked = true;
        }
        const activeCard = getOfferCard(activeInput);
        syncSelectedBundleOptions(activeCard);
        const firstBundleRow = activeCard?.querySelector('[data-bundle-item]');
        const variant = firstBundleRow ? getBundleItemVariant(firstBundleRow) : getSelectedVariant();
        if (!variant) return;
        if (variantInput?.tagName === 'SELECT' && firstBundleRow) variantInput.value = variant.id;

        if (priceNode) priceNode.textContent = variant.formattedPrice || formatMoney(variant.price, root);
        if (compareNode) {
          compareNode.textContent = variant.compareAt > variant.price
            ? variant.formattedCompareAt || formatMoney(variant.compareAt, root)
            : '';
          compareNode.hidden = variant.compareAt <= variant.price;
        }
        if (saleBadge) saleBadge.hidden = variant.compareAt <= variant.price;
        if (savingsBadge) {
          const onSale = variant.compareAt > variant.price;
          savingsBadge.hidden = !onSale;
          if (onSale) {
            const percent = Math.round((variant.compareAt - variant.price) * 100 / variant.compareAt);
            savingsBadge.textContent = root.dataset.offerSavingsPercentFormat.replace('__percent__', String(percent));
          }
        }
        const unitPriceNode = root.querySelector('[data-unit-price]');
        if (unitPriceNode) {
          unitPriceNode.textContent = variant.unitPriceLabel;
          unitPriceNode.hidden = !variant.unitPriceLabel;
        }

        const invalidOffer = offerInputs.length > 0 && (!activeInput || !isOfferValid(activeCard));
        const unavailable = !variant.available || invalidOffer;
        const actionLabel = !variant.available
          ? root.dataset.soldOutLabel
          : invalidOffer ? root.dataset.offerUnavailableLabel : root.dataset.addLabel;
        if (addButton) {
          addButton.disabled = unavailable || pending;
          const label = addButton.querySelector('[data-add-label]');
          if (label) label.textContent = actionLabel;
        }
        if (addPrice) {
          addPrice.hidden = unavailable;
          if (!unavailable) {
            const discountPercent = Number(activeInput.dataset.discountPercent || 0);
            const pricing = offerPricing(getOfferVariants(activeCard), discountPercent);
            addPrice.textContent = formatMoney(pricing.total, root);
          }
        }
        updateQuantity();

        if (variantInput?.tagName === 'SELECT' && window.history && window.URL) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', url);
        }
      };

      const closeBundleOptionDropdowns = () => {
        bundleOptionDropdowns.forEach((dropdown) => {
          const trigger = dropdown.querySelector('[data-bundle-option-trigger]');
          const menu = dropdown.querySelector('[role="listbox"]');
          if (!trigger || !menu) return;
          trigger.setAttribute('aria-expanded', 'false');
          menu.hidden = true;
        });
      };

      bundleOptionDropdowns.forEach((dropdown) => {
        const select = dropdown.querySelector('select[data-bundle-option]');
        const trigger = dropdown.querySelector('[data-bundle-option-trigger]');
        const menu = dropdown.querySelector('[role="listbox"]');
        if (!select || !trigger || !menu) return;
        syncBundleOptionDropdown(select);

        trigger.addEventListener('click', () => {
          const shouldOpen = menu.hidden;
          closeBundleOptionDropdowns();
          if (!shouldOpen) return;
          menu.hidden = false;
          trigger.setAttribute('aria-expanded', 'true');
          menu.querySelector('[aria-selected="true"]:not(:disabled)')?.focus({ preventScroll: true });
        });

        menu.addEventListener('click', (event) => {
          const choice = event.target.closest('[data-bundle-option-choice]');
          if (!choice || choice.disabled || select.disabled) return;
          select.value = choice.dataset.optionValue;
          select.dispatchEvent(new Event('change', { bubbles: true }));
          closeBundleOptionDropdowns();
          trigger.focus({ preventScroll: true });
        });

        menu.addEventListener('keydown', (event) => {
          if (event.key === 'Escape') {
            event.preventDefault();
            closeBundleOptionDropdowns();
            trigger.focus({ preventScroll: true });
            return;
          }

          const choices = [...menu.querySelectorAll('[data-bundle-option-choice]:not(:disabled)')];
          const currentIndex = choices.indexOf(event.target.closest('[data-bundle-option-choice]'));
          let nextIndex = -1;
          if (event.key === 'ArrowDown') nextIndex = Math.min(currentIndex + 1, choices.length - 1);
          if (event.key === 'ArrowUp') nextIndex = Math.max(currentIndex - 1, 0);
          if (event.key === 'Home') nextIndex = 0;
          if (event.key === 'End') nextIndex = choices.length - 1;
          if (nextIndex >= 0) {
            event.preventDefault();
            choices[nextIndex]?.focus();
          }
        });

        dropdown.addEventListener('focusout', (event) => {
          if (!dropdown.contains(event.relatedTarget)) closeBundleOptionDropdowns();
        });
      });

      root.addEventListener('click', (event) => {
        if (!event.target.closest('[data-bundle-select]')) closeBundleOptionDropdowns();
      });

      if (variantInput?.tagName === 'SELECT' && offerInputs.length === 0) {
        variantInput.addEventListener('change', () => {
          if (!pending) updateVariant();
        });
      }
      bundleOptionControls.forEach((control) => control.addEventListener('change', () => {
        if (pending) return;
        const row = control.closest('[data-bundle-item]');
        updateBundleRow(row, control);
        updateOfferPrices();
        const offerInput = row?.closest('[data-offer-card]')?.querySelector('[data-offer-quantity]');
        if (offerInput?.checked) updateVariant();
        else updateQuantity();
      }));
      offerInputs.forEach((input) => input.addEventListener('change', () => {
        if (!pending) updateVariant();
      }));

      window.KindaLovelyProductGallery.init(root, reducedMotion);

      const setPending = (value) => {
        pending = value;
        if (variantInput?.tagName === 'SELECT') variantInput.disabled = value;
        if (value) {
          closeBundleOptionDropdowns();
          offerInputs.forEach((input) => { input.disabled = true; });
          bundleOptionControls.forEach((control) => {
            control.disabled = true;
            syncBundleOptionDropdown(control);
          });
        } else {
          bundleOptionControls.forEach((control) => {
            control.disabled = false;
            syncBundleOptionDropdown(control);
          });
          bundleRows.forEach((row) => updateBundleRowAvailability(row));
          updateOfferPrices();
        }
        const invalid = !getCartLineItems();
        if (addButton) {
          addButton.setAttribute('aria-busy', String(value));
          addButton.disabled = value || invalid;
        }
      };

      window.KindaLovelyProductCart.init(root, { form, cartDialog, offerInputs, getCartLineItems, isPending: () => pending, setPending });

      bundleRows.forEach((row) => updateBundleRowAvailability(row));
      updateVariant();
    };

    const initAll = (scope = document) => scope.querySelectorAll('[data-pdp]').forEach(initProduct);
    const initPage = () => {
      initAll();
    };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initPage, { once: true });
    else initPage();
    document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  })();
